/**
 * Choosing black or white text for a background somebody picked in a colour
 * wheel.
 *
 * The floor plan lets a manager colour a table any hex they like, and a table
 * labelled in white on pale yellow is a table nobody can read across the room.
 * Ported from legacy `pages/table-map.tsx`; it lives here because it is
 * presentation, and the same choice comes up wherever user-chosen colour meets
 * text.
 */

/** The CSS variable that means "whatever the theme's text colour is". */
const THEME_FOREGROUND = 'hsl(var(--foreground))'

/**
 * The ITU-R BT.601 luma weights — the same ones legacy used. Not the modern
 * WCAG relative-luminance formula: switching would flip the text colour on
 * mid-tone tables that staff already know, for a contrast gain nobody asked
 * for.
 */
const RED_WEIGHT = 299
const GREEN_WEIGHT = 587
const BLUE_WEIGHT = 114
const MIDPOINT = 128

export function readableTextColor(hex: string | null | undefined): string {
  const digits = (hex ?? '').replace('#', '')
  if (digits.length !== 6) return THEME_FOREGROUND

  const red = Number.parseInt(digits.slice(0, 2), 16)
  const green = Number.parseInt(digits.slice(2, 4), 16)
  const blue = Number.parseInt(digits.slice(4, 6), 16)
  if (!Number.isFinite(red) || !Number.isFinite(green) || !Number.isFinite(blue)) {
    return THEME_FOREGROUND
  }

  const luma = (red * RED_WEIGHT + green * GREEN_WEIGHT + blue * BLUE_WEIGHT) / 1000
  return luma >= MIDPOINT ? '#000000' : '#ffffff'
}
